import React, { useEffect, useState } from 'react';
import supabase from '../supabase/supabase';
import { FiTrash2, FiCheckCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export default function AdminReports({ refreshKey, onReportHandled }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [refreshKey]);

  const fetchData = async () => {
    const { data, error } = await supabase
      .from('reports_1715000000')
      .select('*, posts_1715000000(image_url, client, title)')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    
    if (data) setReports(data);
    setLoading(false);
  };

  const handleDismiss = async (id) => {
    if (!confirm('Dismiss this report?')) return;
    setProcessingId(id);
    try {
      await supabase.from('reports_1715000000').update({ status: 'resolved' }).eq('id', id);
      fetchData();
      if(onReportHandled) onReportHandled();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeletePost = async (report) => {
    if (!confirm('WARNING: This will permanently delete the reported graphic. Proceed?')) return;
    setProcessingId(report.id);
    try {
      // Deleting the post will cascade delete the report
      await supabase.from('posts_1715000000').delete().eq('id', report.post_id);
      fetchData();
      if(onReportHandled) onReportHandled();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) return <div className="font-bold text-retro-brown bg-white p-8 rounded-3xl border-4 border-retro-brown shadow-retro">Loading reports...</div>;

  return (
    <div className="bg-white p-8 rounded-3xl shadow-retro border-4 border-retro-brown overflow-hidden">
      <h2 className="font-display text-2xl tracking-wider text-retro-brown uppercase mb-6 drop-shadow-[1px_1px_0_#F4D03F]">Reported Listings</h2>
      
      <div className="space-y-6">
        {reports.map((report) => (
          <div key={report.id} className="bg-retro-cream border-4 border-retro-brown rounded-2xl p-6 shadow-retro-sm flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/4 max-w-[200px] flex-shrink-0">
              <div className="border-4 border-retro-brown rounded-xl overflow-hidden shadow-retro-sm bg-gray-100">
                {report.posts_1715000000?.image_url ? (
                  <img src={report.posts_1715000000.image_url} alt="Reported Post" className="w-full h-auto object-cover" />
                ) : (
                  <div className="p-10 text-center font-bold text-retro-brown/50">Image Missing</div>
                )}
              </div>
            </div>

            <div className="flex-1 flex flex-col gap-4">
              <div className="bg-red-50 border-2 border-red-300 p-4 rounded-xl shadow-retro-sm">
                <p className="font-bold text-red-800/60 text-sm mb-1">Reported By: <span className="text-red-900">{report.email}</span></p>
                <div className="mt-2">
                  <p className="font-bold text-red-900 text-sm mb-1">Reason:</p>
                  <p className="font-bold text-red-950 bg-white border-2 border-red-200 p-3 rounded-lg">{report.reason}</p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-auto pt-4 border-t-2 border-retro-brown/20">
                <button 
                  onClick={() => handleDismiss(report.id)}
                  disabled={processingId === report.id}
                  className="px-6 py-3 bg-white text-retro-brown border-2 border-retro-brown rounded-xl font-bold shadow-retro-sm hover:shadow-retro hover:-translate-y-0.5 hover:bg-retro-lime active:translate-y-1 active:shadow-retro-active transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  <SafeIcon icon={FiCheckCircle} /> DISMISS REPORT
                </button>
                <button 
                  onClick={() => handleDeletePost(report)}
                  disabled={processingId === report.id}
                  className="px-6 py-3 bg-red-600 text-white border-2 border-retro-brown rounded-xl font-bold shadow-retro-sm hover:shadow-retro hover:-translate-y-0.5 active:translate-y-1 active:shadow-retro-active transition-all disabled:opacity-50 flex items-center gap-2 tracking-wide uppercase"
                >
                  <SafeIcon icon={FiTrash2} /> DELETE LISTING
                </button>
              </div>
            </div>
          </div>
        ))}
        {reports.length === 0 && (
          <div className="p-10 text-center font-bold text-xl text-retro-brown bg-retro-cream border-4 border-dashed border-retro-brown/30 rounded-2xl">
            No active reports.
          </div>
        )}
      </div>
    </div>
  );
}